import React from 'react';
import { ChevronDown, Grid3X3, CircleDot } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SidebarFilterButton } from '@/components/ui/SidebarFilterButtons';
import { ColorVariant } from '@/components/ui/SidebarFilterButtons';
import { getColorClass } from '@/components/ui/ColorPicker';
import { getCategoryIcon, type CategoryIcon } from '@/components/ui/IconPicker';
import CountSkeleton from '@/components/ui/CountSkeleton';

interface FoldersDropdownProps {
  selectedCategory: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  categories?: Array<{
    id: string;
    name: string;
    color: string;
    icon?: string;
  }>;
  counts?: {
    total?: number;
    categories: Record<string, number>;
  };
  isLoadingCategories?: boolean;
  isLoadingCounts?: boolean;
}

const FoldersDropdown: React.FC<FoldersDropdownProps> = ({
  selectedCategory,
  onCategoryChange,
  categories = [],
  counts,
  isLoadingCategories = false,
  isLoadingCounts = false,
}) => {
  // Get selected category info for trigger display
  const selectedCategoryInfo = selectedCategory 
    ? categories.find(cat => cat.id === selectedCategory)
    : null;

  // Display text for trigger
  const triggerText = selectedCategoryInfo ? selectedCategoryInfo.name : 'All Folders';
  const triggerIcon = selectedCategoryInfo 
    ? getCategoryIcon((selectedCategoryInfo.icon as CategoryIcon) || 'folder')
    : Grid3X3;
  const triggerVariant = selectedCategoryInfo 
    ? (selectedCategoryInfo.color as ColorVariant)
    : 'orange';
  const triggerCount = selectedCategory 
    ? (counts?.categories[selectedCategory] || 0)
    : (counts?.total || 0);

  const TriggerIcon = triggerIcon;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={cn(
            "w-full flex items-center justify-between px-4 py-2 rounded-md text-sm font-medium",
            "bg-white/30 dark:bg-white/10 backdrop-blur-sm",
            "border border-accent/20 focus:border-accent/40",
            "text-slate-700 dark:text-slate-200",
            "shadow-sm hover:shadow-md transition-all duration-300",
            "gap-3"
          )}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <TriggerIcon 
              size={16} 
              className={cn(
                selectedCategoryInfo 
                  ? getColorClass(selectedCategoryInfo.color as ColorVariant)
                  : 'text-orange-500'
              )} 
            />
            <span className="truncate">{triggerText}</span>
            {!isLoadingCounts && (
              <span className={cn(
                'ml-auto px-2 py-0.5 text-xs font-medium rounded-full',
                selectedCategoryInfo
                  ? `bg-${selectedCategoryInfo.color}-100 dark:bg-${selectedCategoryInfo.color}-900/40 text-${selectedCategoryInfo.color}-700 dark:text-${selectedCategoryInfo.color}-300`
                  : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
              )}>
                {triggerCount}
              </span>
            )}
          </div>
          <ChevronDown size={16} className="text-accent flex-shrink-0" />
        </motion.button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="w-[var(--radix-dropdown-menu-trigger-width)] bg-white/90 dark:bg-black/90 backdrop-blur-md border border-accent/20"
        sideOffset={4}
      >
        <div className="p-1 space-y-1">
          {/* All Folders option */}
          <SidebarFilterButton
            variant="orange"
            isActive={selectedCategory === null}
            count={isLoadingCounts ? undefined : counts?.total}
            countElement={isLoadingCounts ? <CountSkeleton /> : undefined}
            onClick={() => onCategoryChange(null)}
            className="w-full"
          >
            <Grid3X3 className="text-orange-500" />
            <span>All Folders</span>
          </SidebarFilterButton>
          
          {/* Loading state for categories */}
          {isLoadingCategories ? (
            <div className="space-y-1">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-9 rounded-md border border-slate-200/50 dark:border-slate-700/50 p-3 flex items-center gap-3">
                  <CircleDot className="h-4 w-4 text-slate-400" />
                  <CountSkeleton className="h-4 flex-1" />
                </div>
              ))}
            </div>
          ) : (
            /* Category options */
            categories.map((cat) => {
              const IconComponent = getCategoryIcon((cat.icon as CategoryIcon) || 'folder');
              return (
                <SidebarFilterButton
                  key={cat.id}
                  variant={cat.color as ColorVariant}
                  isActive={selectedCategory === cat.id}
                  count={isLoadingCounts ? undefined : counts?.categories[cat.id] || 0}
                  countElement={isLoadingCounts ? <CountSkeleton /> : undefined}
                  onClick={() => onCategoryChange(cat.id)}
                  className="w-full"
                >
                  <IconComponent className={getColorClass(cat.color as ColorVariant)} />
                  <span>{cat.name}</span>
                </SidebarFilterButton>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FoldersDropdown; 